import { createRealtimeClient, type RealtimeClient, type RealtimeContext } from './client'
import { registerCoreHandlers } from './handlers/core'
import { registerDashboardHandlers } from './handlers/dashboard'
import { registerDatabaseHandlers } from './handlers/database'
import { registerRowHandlers } from './handlers/rows'

/**
 * The one websocket for this tab.
 *
 * Created outside React on purpose. The `web_socket_id` query parameter is what
 * stops the server echoing a client its own mutations, and it is per-connection:
 * two sockets are two ids, and the second one is told about the first one's
 * writes. Under React 19 StrictMode an effect that owned the socket would mount,
 * tear down and mount again, so a component-owned connection produces exactly
 * that -- optimistic edits applied twice, and no way to tell from the payload
 * that it happened.
 *
 * Access is ref-counted so several mounted consumers share the connection, and
 * the teardown is deferred by a task so StrictMode's immediate remount reclaims
 * the live socket rather than closing and reopening it. Deferring also covers
 * the ordinary case of a route change that unmounts one consumer and mounts
 * another in the same tick.
 */
let client: RealtimeClient | null = null
let refCount = 0
let pendingTeardown: ReturnType<typeof setTimeout> | null = null
/**
 * Whether the connection is currently ours to keep.
 *
 * Not the same as the ref count. Cancelling a deferred teardown returns the
 * count to one over a socket that was never closed, and connecting again there
 * would re-run the whole opening sequence against a live connection.
 */
let connectionOpen = false

export function getRealtimeClient(): RealtimeClient {
  if (client === null) {
    client = createRealtimeClient()
    // Registered once with the client, not per consumer: the handlers are a
    // property of the protocol, and registering them on acquire would run each
    // one once per mounted consumer.
    registerCoreHandlers(client)
    registerDatabaseHandlers(client)
    registerRowHandlers(client)
    registerDashboardHandlers(client)
  }
  return client
}

export interface AcquireOptions {
  anonymous?: boolean
}

/**
 * Takes a reference to the connection, opening it if this is the first.
 *
 * @returns the release function. Calling it twice releases once.
 */
export function acquireRealtime(
  context: RealtimeContext,
  options: AcquireOptions = {},
): () => void {
  if (pendingTeardown !== null) {
    clearTimeout(pendingTeardown)
    pendingTeardown = null
  }

  const active = getRealtimeClient()
  // Re-set every time: a later consumer may hold a different query cache, and the
  // most recent one is the one whose components are on screen.
  active.setContext(context)

  refCount += 1
  if (refCount === 1 && !connectionOpen) {
    connectionOpen = true
    active.connect({ reconnect: true, anonymous: options.anonymous ?? false })
  }

  let released = false
  return () => {
    if (released) return
    released = true
    refCount -= 1
    if (refCount > 0) return

    pendingTeardown = setTimeout(() => {
      pendingTeardown = null
      if (refCount > 0) return
      connectionOpen = false
      active.setContext(null)
      active.disconnect()
    }, 0)
  }
}

/**
 * Test seam: drops the singleton so each spec starts from a closed connection.
 *
 * Pass a replacement to drive the ref counting without opening a socket -- the
 * default client would reach for the global `WebSocket`.
 */
export function __resetRealtimeForTests(replacement: RealtimeClient | null = null): void {
  if (pendingTeardown !== null) {
    clearTimeout(pendingTeardown)
    pendingTeardown = null
  }
  if (replacement === null) client?.disconnect()
  client = replacement
  refCount = 0
  connectionOpen = false
}
